import React, {createContext, useContext, useState, useCallback} from 'react';
import {AppError, ErrorType} from '../../Data/config/AppError';

export interface ErrorContextType {
  error: AppError | null;
  showError: (error: AppError) => void;
  clearError: () => void;
  isNetworkError: boolean;
  isValidationError: boolean;
  isServerError: boolean;
  isAuthenticationError: boolean;
}

export const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const ErrorProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
  const [error, setError] = useState<AppError | null>(null);

  const showError = useCallback((error: AppError) => {
    setError(error);
    // Auto clear error after 5 seconds for non-critical errors
    if (error.type !== ErrorType.AUTHENTICATION) {
      setTimeout(() => {
        setError(null);
      }, 5000);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const isNetworkError = error?.type === ErrorType.NETWORK;
  const isValidationError = error?.type === ErrorType.VALIDATION;
  const isServerError = error?.type === ErrorType.SERVER;
  const isAuthenticationError = error?.type === ErrorType.AUTHENTICATION;

  return (
    <ErrorContext.Provider
      value={{
        error,
        showError,
        clearError,
        isNetworkError,
        isValidationError,
        isServerError,
        isAuthenticationError
      }}
    >
      {children}
    </ErrorContext.Provider>
  );
};

export const useError = () => {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};
